import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CATEGORIES, CATEGORY_KEYS, ROLE_LABELS, ROLE_TO_PEOPLE, RAMADHAN_SUBS, ZAKAT_JENIS, catOf } from "@/lib/activity";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { ListEditor } from "./ListEditor";
import { QurbanEditor } from "./QurbanEditor";
import { BannerPicker } from "./BannerPicker";

const NONE = "__none__";
const FIELD_META = { time_end: { label: "Jam Selesai", type: "time" }, location: { label: "Lokasi" }, pic: { label: "Penanggung Jawab" }, description: { label: "Keterangan", type: "textarea" } };
const WEEKDAYS = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];

const emptyForm = (category = "kajian_mingguan") => ({
  category, title: "", date: new Date().toISOString().slice(0, 10), time: "", time_end: "", location: "Masjid Al-Iman", pic: "", description: "",
  status: "terjadwal", roles: {}, details: {}, recurrence: null, banner_path: "",
});

const F = ({ label, children, full }) => <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label>{label}</Label>{children}</div>;

export const ActivityForm = ({ initial, onSaved, onCancel }) => {
  const [form, setForm] = useState(initial ? { ...emptyForm(initial.category), ...initial, roles: initial.roles || {}, details: initial.details || {} } : emptyForm());
  const [people, setPeople] = useState([]);
  const [repeat, setRepeat] = useState(false);
  const [count, setCount] = useState(8);
  const [saving, setSaving] = useState(false);
  const cfg = catOf(form.category);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setD = (k, v) => setForm((f) => ({ ...f, details: { ...f.details, [k]: v } }));
  const setRole = (r, v) => setForm((f) => ({ ...f, roles: { ...f.roles, [r]: v === NONE ? "" : v } }));
  const peopleOf = (role) => people.filter((p) => p.role === ROLE_TO_PEOPLE[role]);

  useEffect(() => {
    api.get("/people").then((r) => setPeople(r.data)).catch(() => {});
  }, []);

  const changeCategory = (c) => setForm((f) => ({ ...emptyForm(c), date: f.date, time: f.time, banner_path: f.banner_path, title: CATEGORIES[c].defaultTitle || "" }));

  const PersonSelect = ({ role }) => (
    <F label={ROLE_LABELS[role]}>
      <Select value={form.roles[role] || NONE} onValueChange={(v) => setRole(role, v)}>
        <SelectTrigger data-testid={`activity-role-${role}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>— Belum ditentukan —</SelectItem>
          {peopleOf(role).map((p) => <SelectItem key={p.id} value={p.id} data-testid={`activity-role-${role}-option-${p.id}`}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </F>
  );

  const roles = form.category === "ramadhan" ? (form.details.sub === "kuliah_subuh" ? ["penceramah"] : []) : cfg.roles;
  const weekday = form.date ? WEEKDAYS[new Date(form.date + "T00:00:00").getDay()] : "";

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Nama kegiatan wajib diisi");
    setSaving(true);
    const payload = { ...form, recurrence: !initial && cfg.recurrence && repeat ? { type: cfg.recurrence, count: Number(count) } : null };
    delete payload.people; delete payload.people_map; delete payload.attendee_count; delete payload.id; delete payload.created_at; delete payload.reminders;
    try {
      if (initial?.id) await api.put(`/activities/${initial.id}`, payload);
      else {
        const { data } = await api.post("/activities", payload);
        if (data.length > 1) toast.success(`${data.length} jadwal kegiatan dibuat`);
      }
      toast.success(initial ? "Kegiatan diperbarui" : "Kegiatan ditambahkan");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="activity-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="Kategori Kegiatan" full>
          <Select value={form.category} onValueChange={changeCategory} disabled={!!initial}>
            <SelectTrigger data-testid="activity-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORY_KEYS.map((k) => <SelectItem key={k} value={k} data-testid={`activity-category-option-${k}`}>{CATEGORIES[k].label}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        {form.category === "ramadhan" && (
          <F label="Sub-kegiatan Ramadhan" full>
            <Select value={form.details.sub || ""} onValueChange={(v) => setD("sub", v)}>
              <SelectTrigger data-testid="activity-ramadhan-sub"><SelectValue placeholder="Pilih sub-kegiatan" /></SelectTrigger>
              <SelectContent>{Object.entries(RAMADHAN_SUBS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        )}
        <F label="Nama Kegiatan / Acara" full><Input value={form.title} onChange={(e) => set("title", e.target.value)} required placeholder={cfg.defaultTitle || "Contoh: Kajian Kitab Riyadhus Shalihin"} data-testid="activity-title" /></F>
        <F label={cfg.recurrence ? "Tanggal Mulai" : "Tanggal"}><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} required data-testid="activity-date" /></F>
        <F label="Jam Mulai"><Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} data-testid="activity-time" /></F>
        {cfg.fields.filter((f) => f !== "description").map((f) => (
          <F key={f} label={FIELD_META[f].label}><Input type={FIELD_META[f].type || "text"} value={form[f] ?? ""} onChange={(e) => set(f, e.target.value)} data-testid={`activity-${f}`} /></F>
        ))}
        {(cfg.detailFields || []).map((f) => (
          <F key={f.name} label={f.label}><Input value={form.details[f.name] ?? ""} onChange={(e) => setD(f.name, e.target.value)} placeholder={f.placeholder} data-testid={`activity-detail-${f.name}`} /></F>
        ))}
        {roles.map((r) => <PersonSelect key={r} role={r} />)}
        {form.category === "ramadhan" && form.details.sub === "itikaf" && <F label="Penanggung Jawab"><Input value={form.pic} onChange={(e) => set("pic", e.target.value)} data-testid="activity-pic" /></F>}
        {form.category === "ramadhan" && form.details.sub === "tarawih" && (
          <ListEditor label="Jadwal Imam Tarawih per Malam" testId="tarawih-list" rows={form.details.tarawih || []} onChange={(v) => setD("tarawih", v)} blank={{ malam: String((form.details.tarawih || []).length + 1), date: "", imam_id: "" }}
            columns={[{ key: "malam", label: "Malam ke", flex: 0.5 }, { key: "date", label: "Tanggal", type: "date" }, { key: "imam_id", label: "Imam", type: "select", flex: 1.5, options: peopleOf("imam").map((p) => ({ value: p.id, label: p.name })) }]} />
        )}
        {form.category === "qurban" && (
          <>
            <QurbanEditor animals={form.details.animals || []} onChange={(v) => setD("animals", v)} />
            <F label="Penjagal (pisahkan dengan koma)"><Input value={(form.details.butchers || []).join(", ")} onChange={(e) => setD("butchers", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="qurban-butchers" /></F>
            <F label="Tim Penjagal (pisahkan dengan koma)"><Input value={(form.details.team || []).join(", ")} onChange={(e) => setD("team", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="qurban-team" /></F>
          </>
        )}
        {form.category === "zakat_fitrah" && (
          <>
            <F label="Periode Penerimaan Mulai"><Input type="date" value={form.details.period_start || ""} onChange={(e) => setD("period_start", e.target.value)} data-testid="zakat-period-start" /></F>
            <F label="Periode Penerimaan Selesai"><Input type="date" value={form.details.period_end || ""} onChange={(e) => setD("period_end", e.target.value)} data-testid="zakat-period-end" /></F>
            <F label="Petugas Penerima" full><Input value={form.details.petugas || ""} onChange={(e) => setD("petugas", e.target.value)} placeholder="Nama-nama petugas / amil" data-testid="zakat-petugas" /></F>
            <ListEditor label="Data Muzakki" testId="muzakki-list" rows={form.details.muzakki || []} onChange={(v) => setD("muzakki", v)} blank={{ name: "", jumlah: "", jenis: "beras" }}
              columns={[{ key: "name", label: "Nama", flex: 2 }, { key: "jumlah", label: "Jumlah", type: "number" }, { key: "jenis", label: "Jenis", type: "select", options: Object.entries(ZAKAT_JENIS).map(([k, v]) => ({ value: k, label: v })) }]} />
            <ListEditor label="Data Mustahiq & Distribusi" testId="mustahiq-list" rows={form.details.mustahiq || []} onChange={(v) => setD("mustahiq", v)} blank={{ name: "", jumlah: "", keterangan: "" }}
              columns={[{ key: "name", label: "Nama", flex: 2 }, { key: "jumlah", label: "Jumlah (mis. 2,5 kg)" }, { key: "keterangan", label: "Keterangan" }]} />
          </>
        )}
        {cfg.fields.includes("description") && <F label="Keterangan / Deskripsi" full><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="activity-description" /></F>}
        <BannerPicker value={form.banner_path} onChange={(v) => set("banner_path", v)} category={form.category} />
        {!initial && cfg.recurrence && (
          <div className="sm:col-span-2 rounded-2xl bg-emerald-50 border border-emerald-900/10 p-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3">
              <Switch checked={repeat} onCheckedChange={setRepeat} data-testid="activity-repeat-switch" />
              <span className="text-sm font-medium">Generate otomatis setiap {cfg.recurrence === "weekly" ? "minggu" : "2 minggu"}{weekday ? ` (hari ${weekday})` : ""}</span>
            </div>
            {repeat && <div className="flex items-center gap-2 text-sm"><span>Jumlah pertemuan</span><Input type="number" min={1} max={52} value={count} onChange={(e) => setCount(e.target.value)} className="h-9 w-20" data-testid="activity-repeat-count" /></div>}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" className="rounded-full" onClick={onCancel} data-testid="activity-cancel-btn">Batal</Button>
        <Button type="submit" disabled={saving} className="rounded-full emerald-bg hover:bg-[#145c48]" data-testid="activity-save-btn">{saving ? <Loader2 className="animate-spin" size={16} /> : "Simpan"}</Button>
      </DialogFooter>
    </form>
  );
};
